import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ModeMeta, fmtN } from 'pages/Learning/learningMeta';
import LearningModeIcon from 'pages/Learning/components/LearningModeIcon';

interface LearningModeCardProps {
  mode: ModeMeta;
  count?: number;
  recommended?: boolean;
  onStart: () => void;
  disabled?: boolean;
  /** Override for the start-button label on non-actionable modes. Defaults
   *  to "Почати"; the page passes "Почати нову сесію" when there is an
   *  in-progress session that will be replaced on click. */
  startLabel?: string;
}

const LearningModeCard: React.FC<LearningModeCardProps> = ({
  mode,
  count,
  recommended,
  onStart,
  disabled,
  startLabel = 'Почати',
}) => {
  const actionable = !!mode.actionable;
  const tint = (alpha: number) => `rgba(59, 44, 147, ${alpha})`;

  return (
    <Box
      sx={{
        bgcolor: actionable ? 'background.default' : 'background.paper',
        border: '1px solid',
        borderColor: recommended ? 'primary.main' : 'primary.tableBorder',
        borderRadius: 2,
        borderStyle: actionable ? 'dashed' : 'solid',
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        opacity: disabled ? 0.6 : 1,
        p: '20px 20px 22px',
        position: 'relative',
        transition: 'all .18s ease',
        '&:hover': {
          borderColor: disabled ? undefined : 'primary.main',
          boxShadow: disabled || actionable ? 'none' : '0 16px 36px -26px rgba(20,20,20,0.4)',
          transform: disabled ? 'none' : 'translateY(-2px)',
        },
      }}
      onClick={disabled ? undefined : onStart}
    >
      {recommended && (
        <Box
          sx={{
            bgcolor: 'primary.main',
            borderRadius: 999,
            color: 'common.white',
            fontSize: 10.5,
            fontWeight: 800,
            left: 18,
            letterSpacing: '0.05em',
            position: 'absolute',
            px: 1.25,
            py: '3px',
            textTransform: 'uppercase',
            top: -10,
          }}
        >
          {'Рекомендовано'}
        </Box>
      )}

      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1.25}
        sx={{ mb: 1.25 }}
      >
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? tint(0.08) : 'rgba(155,124,255,0.18)',
            borderRadius: 1.25,
            color: 'primary.main',
            display: 'inline-flex',
            flex: 'none',
            height: 40,
            justifyContent: 'center',
            width: 40,
          }}
        >
          <LearningModeIcon name={mode.icon} size={23} />
        </Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
          {mode.label}
        </Typography>
      </Stack>

      {!actionable && (
        <Stack
          direction={'row'}
          alignItems={'baseline'}
          spacing={1}
          sx={{ mb: 1.25 }}
        >
          <Typography
            sx={{ color: 'primary.main', fontSize: 26, fontWeight: 800 }}
          >
            {count !== undefined ? fmtN(count) : '∞'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            {mode.countLabel}
          </Typography>
        </Stack>
      )}

      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 13.5,
          lineHeight: 1.45,
          mb: 1.75,
          flex: 1,
        }}
      >
        {mode.desc}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
        <Button
          variant={recommended ? 'contained' : 'outlined'}
          startIcon={actionable ? <ArrowForwardIcon /> : <PlayArrowIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
          disabled={disabled}
          sx={{ height: 40, px: 2 }}
        >
          {actionable ? 'Обрати слова' : startLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default LearningModeCard;
